import * as React from "react";

import { cn } from "@/lib/utils";

export interface InputProps extends React.ComponentProps<"input"> {
  variant?: "default" | "compact" | "compact-wide" | "compact-narrow";
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, variant = "default", ...props }, ref) => {
    const internalRef = React.useRef<HTMLInputElement>(null);
    const mergedRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        internalRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );
    const dragStateRef = React.useRef<{ cleanup: (() => void) | null; dragging: boolean }>({
      cleanup: null,
      dragging: false,
    });

    React.useEffect(() => {
      return () => {
        dragStateRef.current.cleanup?.();
      };
    }, []);

    const updateNumberInputValue = React.useCallback((input: HTMLInputElement, nextValue: number) => {
      const nativeSetter = Object.getOwnPropertyDescriptor(
        HTMLInputElement.prototype,
        "value",
      )?.set;
      if (!nativeSetter) return;
      nativeSetter.call(input, String(nextValue));
      input.dispatchEvent(new Event("input", { bubbles: true }));
    }, []);

    const handleMouseDown = React.useCallback(
      (event: React.MouseEvent<HTMLInputElement>) => {
        props.onMouseDown?.(event);
        if (event.defaultPrevented || type !== "number" || event.button !== 0) return;

        const input = internalRef.current;
        if (!input || document.activeElement === input) return;

        event.preventDefault();

        const step = Number(input.step);
        const min = input.min === "" ? undefined : Number(input.min);
        const max = input.max === "" ? undefined : Number(input.max);
        const startValue = input.value === "" ? 0 : Number(input.value);
        const effectiveStep = Number.isFinite(step) && step > 0 ? step : 1;
        const safeStartValue = Number.isFinite(startValue) ? startValue : 0;
        const startY = event.clientY;
        let hasDragged = false;

        const clearSelection = () => {
          window.getSelection()?.removeAllRanges();
        };

        const cleanup = () => {
          document.removeEventListener("mousemove", onMouseMove);
          document.removeEventListener("mouseup", onMouseUp);
          document.removeEventListener("selectstart", onSelectStart);
          document.body.style.cursor = "";
          document.body.style.userSelect = "";
          dragStateRef.current.cleanup = null;
          dragStateRef.current.dragging = false;
        };

        const onSelectStart = (selectEvent: Event) => {
          selectEvent.preventDefault();
        };

        const onMouseMove = (moveEvent: MouseEvent) => {
          const deltaY = moveEvent.clientY - startY;
          if (!hasDragged && Math.abs(deltaY) < 3) return;

          moveEvent.preventDefault();
          clearSelection();

          if (!hasDragged) {
            hasDragged = true;
            dragStateRef.current.dragging = true;
            document.body.style.cursor = "ns-resize";
            document.body.style.userSelect = "none";
          }

          let nextValue = safeStartValue + Math.round(-deltaY / 2) * effectiveStep;
          if (min !== undefined && Number.isFinite(min) && nextValue < min) nextValue = min;
          if (max !== undefined && Number.isFinite(max) && nextValue > max) nextValue = max;

          updateNumberInputValue(input, nextValue);
        };

        const onMouseUp = () => {
          cleanup();
          if (!hasDragged) {
            input.focus();
            input.select();
          }
        };

        dragStateRef.current.cleanup = cleanup;
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
        document.addEventListener("selectstart", onSelectStart);
      },
      [props, type, updateNumberInputValue],
    );

    const cursorStyle =
      props.style?.cursor ?? (type === "number" ? "ns-resize" : undefined);

    const baseClasses =
      "flex rounded-md border border-input bg-background ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

    const variantClasses =
      variant === "compact"
        ? "h-6 w-20 px-1 py-0 text-xs min-w-[2ch] box-border"
        : variant === "compact-wide"
          ? "h-6 w-full px-1 py-0 text-xs min-w-[2ch] box-border"
          : variant === "compact-narrow"
            ? "h-6 w-12 px-1 py-0 text-xs min-w-[2ch] box-border"
            : "h-10 w-full px-3 py-2 text-base md:text-sm";

    return (
      <input
        {...props}
        type={type}
        className={cn(baseClasses, variantClasses, className)}
        ref={mergedRef}
        onMouseDown={handleMouseDown}
        style={{ ...props.style, cursor: cursorStyle }}
      />
    );
  },
);
Input.displayName = "Input";

export { Input };
